/**
 * Tenant slug resolution — frontend
 * ===================================
 * Reads the tenant identifier from (in priority order):
 *  1. Subdomain of the current hostname  → acme.attendza.com = "acme"
 *  2. localStorage key "attendza_tenant" → manual override (used during login)
 *
 * The resolved slug is sent as `X-Tenant-Slug` on every API request so the
 * backend can look up and inject the matching tenant_id.
 *
 * Super admin logs in at the bare domain (or sets slug to "admin") — the
 * backend handles null slug gracefully for super_admin accounts.
 */

const RESERVED = new Set(['www', 'app', 'api', 'admin', 'mail', 'static', 'assets', 'localhost']);

/** Return the subdomain of the current page hostname, or null. */
function readSubdomain(): string | null {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length < 3) return null;
  const sub = parts[0].toLowerCase();
  if (RESERVED.has(sub)) return null;
  return sub;
}

export const TENANT_STORAGE_KEY = 'attendza_tenant';

/** Get the active tenant slug (subdomain → localStorage → null). */
export function getTenantSlug(): string | null {
  return readSubdomain() ?? localStorage.getItem(TENANT_STORAGE_KEY) ?? null;
}

/** Persist a manually-entered tenant slug (used on the login page). */
export function setTenantSlug(slug: string | null) {
  if (slug) {
    localStorage.setItem(TENANT_STORAGE_KEY, slug.trim().toLowerCase());
  } else {
    localStorage.removeItem(TENANT_STORAGE_KEY);
  }
}

/** True when the slug was resolved from the subdomain (cannot be changed by the user). */
export function isTenantLocked(): boolean {
  return readSubdomain() !== null;
}
